import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Search, Globe, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Country, CountryFormData } from '@/types/geography';
import { useConfirmDialog, createDeleteConfirm } from '@/components/ui/confirm-dialog';
import DataTablePagination from '@/components/ui/data-table-pagination';
import {
  useCountries as useCountriesApi,
  useCountry as useCountryApi,
  useCreateCountry,
  useUpdateCountry,
  useDeleteCountry,
} from '@/api-services/hooks/useCountryRefactored';
import {
  buildCountryPayload,
  mapCountryMasterToCountry,
} from '@/utils/geographyMappers';

const CountriesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [countries, setCountries] = useState<Country[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Form state
  const [formData, setFormData] = useState<CountryFormData>({
    name: '',
    code: '',
    status: 'active'
  });

  // Validation errors state
  const [errors, setErrors] = useState<{
    name?: string;
    code?: string;
    status?: string;
  }>({});

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Country name is required';
    } else {
      // Check for unique name (case-insensitive)
      const isDuplicateName = countries.some(
        country => country.name.toLowerCase() === formData.name.toLowerCase() &&
        country.id !== editingCountry?.id
      );
      if (isDuplicateName) {
        newErrors.name = 'Country with this name already exists';
      }
    }

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'Country code is required';
    } else if (formData.code.length !== 2) {
      newErrors.code = 'Country code must be exactly 2 characters';
    } else {
      // Check for unique code (case-insensitive)
      const isDuplicateCode = countries.some(
        country => country.code.toLowerCase() === formData.code.toLowerCase() &&
        country.id !== editingCountry?.id
      );
      if (isDuplicateCode) {
        newErrors.code = 'Country with this code already exists';
      }
    }

    // Status validation
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time validation on input change
  const handleInputChange = (field: keyof CountryFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const countryQueryParams = useMemo(() => ({ page: 0, size: 1000 }), []);

  const {
    data: countryResponse,
    loading: countriesLoading,
    error: countriesError,
    refetch: refetchCountries,
  } = useCountriesApi(countryQueryParams);

  const createCountryMutation = useCreateCountry();
  const updateCountryMutation = useUpdateCountry();
  const deleteCountryMutation = useDeleteCountry();

  // Fetch existing country data when editing to preserve system IP fields
  const { data: existingCountryData } = useCountryApi(
    editingCountry ? Number(editingCountry.id) : 0,
    {
      enabled: !!editingCountry && !!editingCountry.id,
    }
  );

  const actionLoading =
    createCountryMutation.loading ||
    updateCountryMutation.loading ||
    deleteCountryMutation.loading;

  useEffect(() => {
    if (countryResponse?.content) {
      const mapped = countryResponse.content.map(mapCountryMasterToCountry);
      setCountries(mapped);
    } else {
      setCountries([]);
    }
  }, [countryResponse]);

  useEffect(() => {
    if (countriesError) {
      toast.error(countriesError.message);
    }
  }, [countriesError]);

  // Filter countries based on search and status
  useEffect(() => {
    let filtered = countries;

    if (searchTerm) {
      filtered = filtered.filter(country =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(country => country.status === statusFilter);
    }

    setFilteredCountries(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [countries, searchTerm, statusFilter]);

  // Pagination calculations
  const totalItems = filteredCountries.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCountries = filteredCountries.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      status: 'active'
    });
    setErrors({});
    setEditingCountry(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      if (editingCountry) {
        const payload = await buildCountryPayload(formData, editingCountry.id, existingCountryData || undefined);
        await updateCountryMutation.mutate({
          id: Number(editingCountry.id),
          data: payload,
        });
        toast.success('Country updated successfully!');
      } else {
        const payload = await buildCountryPayload(formData);
        await createCountryMutation.mutate(payload);
        toast.success('Country created successfully!');
      }

      await refetchCountries();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save country';

      // Handle server-side validation errors for duplicates
      if (message.toLowerCase().includes('duplicate') ||
          message.toLowerCase().includes('already exists') ||
          message.toLowerCase().includes('unique')) {
        if (message.toLowerCase().includes('name')) {
          setErrors(prev => ({ ...prev, name: 'Country name already exists' }));
        } else if (message.toLowerCase().includes('code')) {
          setErrors(prev => ({ ...prev, code: 'Country code already exists' }));
        } else {
          // Generic duplicate error
          setErrors(prev => ({
            ...prev,
            name: 'Duplicate country detected',
            code: 'Duplicate country detected'
          }));
        }
      }

      toast.error(message);
    }
  };

  const handleEdit = (country: Country) => {
    setEditingCountry(country);
    setFormData({
      name: country.name,
      code: country.code,
      status: country.status,
      metadata: country.metadata || {}
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (country: Country) => {
    const countryName = country.name;
    confirm(createDeleteConfirm(
      'Country',
      async () => {
        try {
          await deleteCountryMutation.mutate(Number(country.id));
          toast.success(`${countryName} deleted successfully!`);
          // Invalidate all countries queries to ensure fresh data
          await queryClient.invalidateQueries({ queryKey: ['countries'] });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete country';
          toast.error(message);
        }
      },
      `Are you sure you want to delete ${countryName}? This will permanently remove the country and all associated data. This action cannot be undone.`
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Countries</h1>
          <p className="text-muted-foreground">Manage countries and their configurations</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Add Country
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingCountry ? 'Edit Country' : 'Add New Country'}
              </DialogTitle>
              <DialogDescription>
                {editingCountry
                  ? 'Update the country information below.'
                  : 'Add a new country to the system.'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Country Name *</label>
                  <Input
                    key="country-name-input"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onBlur={() => {
                      // Validate on blur for better UX
                      if (formData.name.trim()) {
                        const isDuplicateName = countries.some(
                          country => country.name.toLowerCase() === formData.name.toLowerCase() &&
                          country.id !== editingCountry?.id
                        );
                        if (isDuplicateName) {
                          setErrors(prev => ({ ...prev, name: 'Country with this name already exists' }));
                        }
                      }
                    }}
                    placeholder="United States"
                    className={errors.name ? "border-red-500 focus:ring-red-500" : ""}
                    required
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium">ISO Code (2-char) *</label>
                  <Input
                    key="country-code-input"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    onBlur={() => {
                      // Validate on blur for better UX
                      if (formData.code.trim()) {
                        if (formData.code.length !== 2) {
                          setErrors(prev => ({ ...prev, code: 'Country code must be exactly 2 characters' }));
                        } else {
                          const isDuplicateCode = countries.some(
                            country => country.code.toLowerCase() === formData.code.toLowerCase() &&
                            country.id !== editingCountry?.id
                          );
                          if (isDuplicateCode) {
                            setErrors(prev => ({ ...prev, code: 'Country with this code already exists' }));
                          }
                        }
                      }
                    }}
                    placeholder="US"
                    maxLength={2}
                    className={errors.code ? "border-red-500 focus:ring-red-500" : ""}
                    required
                  />
                  {errors.code && (
                    <p className="text-sm text-red-600 mt-1">{errors.code}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Status *</label>
                  <Select
                    value={formData.status}
                    onValueChange={(value: 'active' | 'inactive') => {
                      handleInputChange('status', value);
                    }}
                  >
                    <SelectTrigger className={errors.status ? "border-red-500 focus:ring-red-500" : ""}>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.status && (
                    <p className="text-sm text-red-600 mt-1">{errors.status}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : (editingCountry ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Countries</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{countries.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Countries</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {countries.filter(c => c.status === 'active').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inactive Countries</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {countries.filter(c => c.status === 'inactive').length}
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Countries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Countries ({filteredCountries.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {countriesLoading && (
            <div className="text-sm text-muted-foreground mb-4">Loading countries...</div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedCountries.map((country) => (
                <TableRow key={country.id}>
                  <TableCell>
                    <div className="font-medium">{country.name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-mono uppercase">{country.code}</div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        country.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}
                    >
                      {country.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(country)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(country)}
                        disabled={actionLoading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!countriesLoading && filteredCountries.length === 0 && (
            <div className="text-center py-8">
              <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No countries found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by adding your first country'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {filteredCountries.length > 0 && (
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
};

export default CountriesPage;
